<template>
  <div>
    <v-autocomplete :md-options="envelopeOptions" v-model="selectedEnvelope" md-input-name="envelope" md-dense @md-opened="sizeProperly">
      <label>{{ envelopeLabel }}</label>
    </v-autocomplete>
    <span class="md-error envelope-error" v-if="!isEnvelopeSelected">Izberite kuverto iz seznama ali pustite polje prazno</span>

    <v-field :class="getValidationClass('name')">
      <label>Naziv dokumenta</label>
      <v-input v-model="form.name" />
      <span class="md-error" v-if="!$v.form.name.required">Vpišite naziv dokumenta</span>
    </v-field>

    <v-field>
      <label>Kratek opis</label>
      <v-input v-model="form.description" />
    </v-field>

    <v-field>
      <label>Zabeležke</label>
      <v-input v-model="form.notes" />
    </v-field>

    <v-field>
      <label>Stranka 1 (Vaše podjetje / Vi)</label>
      <v-input v-model="form.partyName" />
    </v-field>

    <v-field>
      <label>Stranka 2</label>
      <v-input v-model="form.counterpartyName" />
    </v-field>

    <v-field>
      <label>Matična št. ali EMŠO stranke</label>
      <v-input type="number" v-model="form.counterpartyRegistrationNumber" />
    </v-field>

    <v-datepicker v-model="selectedSigningDate" md-immediately>
      <label>Datum podpisa</label>
    </v-datepicker>

    <v-datepicker v-model="selectedEffectiveDate" md-immediately>
      <label>Datum začetka učinkovanja</label>
    </v-datepicker>

    <v-datepicker v-model="selectedExpirationDate" md-immediately>
      <label>Datum prenehanja</label>
      <input-help v-if="selectedExpirationDate === null" text="Če veljavnost dokumenta časovno ni omejena, pustite prazno." :style="{ margin: 'auto' }" />
    </v-datepicker>

    <custom-attributes-form-fields :custom-attributes="customAttributes" :custom-attribute-keys="customAttributeKeys" @change="updateCustomAttributesAttributes" />

    <v-field v-if="selectedPdfFile !== null" :class="getValidationClass('pdfBlobHash')">
      <r-file-upload
        v-model="selectedPdfFile"
        :disabled="isUploadingFile"
        :accepts="['application/pdf']"
        file-upload-prefix="finalized_documents"
        button-text="Naloži PDF datoteko"
        @uploading="onUploading"
        @uploaded="onUploaded"
      />
      <span class="md-error" v-if="!$v.form.pdfBlobHash.required">Izberite PDF datoteko</span>
    </v-field>

    <v-field v-if="selectedDocxFile !== null" :class="getValidationClass('docxBlobHash')">
      <r-file-upload
        v-model="selectedDocxFile"
        :disabled="isUploadingFile"
        :accepts="['application/vnd.openxmlformats-officedocument.wordprocessingml.document']"
        file-upload-prefix="finalized_documents"
        button-text="Naloži DOCX datoteko"
        @uploading="onUploading"
        @uploaded="onUploaded"
      />
      <span class="md-error" v-if="!$v.form.pdfBlobHash.required">Izberite DOCX datoteko</span>
    </v-field>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import ValidationMixin from '@/components/shared/ValidationMixin'
import AutocompleteFixMixin from '@/components/shared/AutocompleteFixMixin'
import CustomAttributesFormFields from './CustomAttributesFormFields'
import InputHelp from '@/components/shared/InputHelp'
import VAutocomplete from 'design-system/VAutocomplete.vue'
import VDatepicker from 'design-system/VDatepicker.vue'
import VField from 'design-system/VField.vue'
import VInput from 'design-system/VInput.vue'
import RFileUpload from 'design-system/components/RFileUpload.vue'
import { MIXED_VALUES_LABEL } from '@/utils'

export default {
  data () {
    return {
      isEnvelopeSelected: true,
      selectedPdfFileName: null,
      selectedDocxFileName: null,
      selectedEnvelope: null,
      selectedSigningDate: null,
      selectedExpirationDate: null,
      selectedEffectiveDate: null,
      isUploadingFile: false,
      form: {
        envelope: {
          id: null,
        },
        name: null,
        description: null,
        notes: null,
        partyName: null,
        counterpartyName: null,
        counterpartyRegistrationNumber: null,
        signedOn: null,
        effectiveOn: null,
        expiresOn: null,
        pdfBlobHash: null,
        docxBlobHash: null,
        customAttributesAttributes: [],
      },
    }
  },
  props: {
    finalizedDocument: { type: Object, required: true },
    envelopes: { type: Array, required: true },
    customAttributeKeys: { type: Array, required: true },
  },
  components: {
    CustomAttributesFormFields,
    InputHelp,
    VAutocomplete,
    VDatepicker,
    VField,
    VInput,
    RFileUpload,
  },
  mixins: [ValidationMixin, AutocompleteFixMixin],
  mounted () {
    if (!this.finalizedDocument) {
      return
    }

    this.setFinalizedDocumentAttributes()

    if (this.form.signedOn) {
      const date = new Date(this.form.signedOn)
      this.selectedSigningDate = isNaN(date.getTime()) ? this.form.signedOn : date
    }
    if (this.form.effectiveOn) {
      const date = new Date(this.form.effectiveOn)
      this.selectedEffectiveDate = isNaN(date.getTime()) ? this.form.effectiveOn : date
    }
    if (this.form.expiresOn) {
      const date = new Date(this.form.expiresOn)
      this.selectedExpirationDate = isNaN(date.getTime()) ? this.form.expiresOn : date
    }
  },
  watch: {
    selectedEnvelope: {
      handler (newSelectedEnvelope) {
        if (!newSelectedEnvelope) {
          this.form.envelope.id = null
          this.isEnvelopeSelected = true
          return
        }
        if (typeof newSelectedEnvelope === 'string') {
          this.form.envelope.id = null
          this.isEnvelopeSelected = false
          return
        }

        this.form.envelope.id = newSelectedEnvelope.id
        this.isEnvelopeSelected = true
      },
      immediate: true,
    },
    selectedSigningDate: {
      handler (newSelectedSigningDate) {
        // Bah, this component should not be aware of MIXED_VALUES_LABEL.
        // This internal state should never be emitted outside of stateless components.
        if (newSelectedSigningDate === MIXED_VALUES_LABEL) {
          return
        }

        if (!newSelectedSigningDate) {
          this.form.signedOn = null
          return
        }

        this.form.signedOn = newSelectedSigningDate.toDateString()
        if (!this.selectedEffectiveDate) {
          this.selectedEffectiveDate = newSelectedSigningDate
          this.form.effectiveOn = this.form.signedOn
          this.runAllValidations()
        }
      },
    },
    selectedEffectiveDate: {
      handler (newSelectedEffectiveDate) {
        // Bah, this component should not be aware of MIXED_VALUES_LABEL.
        // This internal state should never be emitted outside of stateless components.
        if (newSelectedEffectiveDate === MIXED_VALUES_LABEL) {
          return
        }

        if (!newSelectedEffectiveDate) {
          this.form.effectiveOn = null
          return
        }

        this.form.effectiveOn = newSelectedEffectiveDate.toDateString()
      },
    },
    selectedExpirationDate: {
      handler (newSelectedExpirationDate) {
        // Bah, this component should not be aware of MIXED_VALUES_LABEL.
        // This internal state should never be emitted outside of stateless components.
        if (newSelectedExpirationDate === MIXED_VALUES_LABEL) {
          return
        }

        if (!newSelectedExpirationDate) {
          this.form.expiresOn = null
          return
        }

        this.form.expiresOn = newSelectedExpirationDate.toDateString()
      },
    },
    form: {
      handler (newFormValue) {
        this.$emit('change', {
          ...this.finalizedDocument,
          ...newFormValue,
        })
      },
      deep: true,
    },
  },
  methods: {
    setFinalizedDocumentAttributes () {
      this.form.name = this.finalizedDocument.name
      this.form.description = this.finalizedDocument.description
      this.form.notes = this.finalizedDocument.notes
      this.form.partyName = this.finalizedDocument.partyName
      this.form.counterpartyName = this.finalizedDocument.counterpartyName
      this.form.counterpartyRegistrationNumber = this.finalizedDocument.counterpartyRegistrationNumber === '' ? null : this.finalizedDocument.counterpartyRegistrationNumber
      this.form.signedOn = this.finalizedDocument.signedOn
      this.form.effectiveOn = this.finalizedDocument.effectiveOn
      this.form.expiresOn = this.finalizedDocument.expiresOn
      this.form.pdfBlobHash = this.finalizedDocument.pdfBlobHash
      this.form.docxBlobHash = this.finalizedDocument.docxBlobHash
      if (this.finalizedDocument.envelopeId) {
        this.selectedEnvelope = this.envelopeOptions.find(option => option.id === this.finalizedDocument.envelopeId)
      }
    },
    runAllValidations () {
      this.$v.$touch()
    },
    isValid () {
      return !this.$v.$invalid && this.isEnvelopeSelected
    },
    isUploading () {
      return this.isUploadingFile
    },
    onUploading () {
      this.$emit('uploading')
      this.isUploadingFile = true
    },
    onUploaded () {
      this.$emit('uploaded')
      this.isUploadingFile = false
    },
    updateCustomAttributesAttributes (newCustomAttributes) {
      this.form.customAttributesAttributes = newCustomAttributes
    },
  },
  computed: {
    selectedPdfFile: {
      get () {
        if (!this.form.pdfBlobHash) {
          return {}
        }

        if (this.form.pdfBlobHash === MIXED_VALUES_LABEL) {
          return null
        }

        return {
          name: this.pdfName,
        }
      },
      set (fileData) {
        this.selectedPdfFileName = fileData.name
        this.form.pdfBlobHash = fileData.blobHash
      },
    },
    selectedDocxFile: {
      get () {
        if (!this.form.docxBlobHash) {
          return {}
        }

        if (this.form.docxBlobHash === MIXED_VALUES_LABEL) {
          return null
        }

        return {
          name: this.docxName,
        }
      },
      set (fileData) {
        this.selectedDocxFileName = fileData.name
        this.form.docxBlobHash = fileData.blobHash
      },
    },
    envelopeOptions () {
      // all the mapping is required due to a bug in the component https://github.com/vuematerial/vue-material/issues/1243
      return this.envelopes.map(this.toAutocompleteItem)
    },
    customAttributes () {
      return this.finalizedDocument.customAttributes ?? []
    },
    pdfName () {
      if (this.selectedPdfFileName) {
        return this.selectedPdfFileName
      }
      return this.form.name ? `${this.form.name}.pdf` : null
    },
    docxName () {
      if (this.selectedDocxFileName) {
        return this.selectedDocxFileName
      }
      return this.form.name ? `${this.form.name}.docx` : null
    },
    envelopeLabel () {
      const suffix = this.finalizedDocument.envelopeId === MIXED_VALUES_LABEL ? 'bo ostala enaka' : 'bo ustvarjena nova'
      return `Pripada kuverti (če kuverte ne izberete, ${suffix})`
    },
  },
  validations: {
    form: {
      name: {
        required,
      },
      pdfBlobHash: {
        required: requiredIf(parent => !parent.docxBlobHash),
      },
      docxBlobHash: {
        required: requiredIf(parent => !parent.pdfBlobHash),
      },
    },
    isUploadingFile: {
      isUploading: value => value === false,
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/components/admin_common';

.envelope-error {
  position: relative;
  top: -16px;
  height: 0;
}
</style>
